import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import type { NurseWithRelation, NurseResponse } from "@/types/nurse-type";
import { compareNurses } from "@/utils/sort-util";
import { nurseRoleSortIndex } from "@/utils/common-util";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_BASE_KEY } from "@/constants/query-key";
import httpClient from "@/config/axios";
import { toast } from "@/components/toast/CustomToast";
import { useIsAdmin } from "./useAuth";
import { useGroupSelection } from "./useGroupSelection";

// 0112 박현민 :  Grdae랑 경력만 처리함 -> 나중에 추가 필요 시 확장 가능
type SortKey = "experience" | "grade" | "name";
type SortOrder = "asc" | "desc" | null;

export interface SortConfig {
  key: SortKey | null;
  order: SortOrder;
}

/**
 * 정렬 가능한 간호사 목록 훅
 * - 정렬 시 낙관적 UI 업데이트
 * - sequence 재할당
 * - 디바운스 후 bulk-update API 호출
 */
export function useSortableNurses<T extends NurseWithRelation>(
  nurses: T[],
  options?: {
    enableSequenceUpdate?: boolean; // sequence 업데이트 활성화 여부
    debounceMs?: number; // 디바운스 시간 (기본 500ms)
    keepRoleGroupOrder?: boolean; // RN/AN/ETC 그룹 순서 고정 여부
  }
) {
  const {
    enableSequenceUpdate = true,
    debounceMs = 500,
    keepRoleGroupOrder = false,
  } = options ?? {};

  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const { selectedGroupId } = useGroupSelection();

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    order: null,
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdateRef = useRef<NurseResponse[] | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // 정렬된 간호사 목록 (낙관적 업데이트용)
  const sortedNurses = useMemo(() => {
    if (!sortConfig.key || !sortConfig.order) return nurses;

    const sorter = (a: T, b: T) => {
      if (keepRoleGroupOrder) {
        const roleDiff =
          nurseRoleSortIndex(a.role) - nurseRoleSortIndex(b.role);
        if (roleDiff !== 0) return roleDiff;
      }
      return compareNurses(a, b, sortConfig.key!, sortConfig.order as "asc" | "desc");
    };

    return [...nurses].sort(sorter);
  }, [nurses, sortConfig, keepRoleGroupOrder]);

  // 정렬 키/순서 라벨 생성
  const getSortLabel = (key: SortKey, order: "asc" | "desc") => {
    const keyLabel =
      key === "grade" ? "Grade" : key === "experience" ? "경력" : "이름";
    const orderLabel = order === "asc" ? "오름차순" : "내림차순";
    return `${keyLabel} ${orderLabel}`;
  };

  // 마지막 정렬 정보 저장 (토스트 메시지용)
  const lastSortInfoRef = useRef<{
    key: SortKey;
    order: "asc" | "desc";
  } | null>(null);

  // Bulk update API 호출
  const executeBulkUpdate = useCallback(
    async (nursesToUpdate: NurseResponse[]) => {
      if (nursesToUpdate.length === 0) return;

      setIsUpdating(true);
      try {
        const groupId = isAdmin ? selectedGroupId : null;
        const url = groupId
          ? `/nurses/bulk-update?group_id=${encodeURIComponent(String(groupId))}`
          : `/nurses/bulk-update`;

        await httpClient.post(url, nursesToUpdate);

        // 캐시 무효화
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.NURSES, selectedGroupId],
        });

        // 상세 정렬 정보 포함 토스트
        const sortInfo = lastSortInfoRef.current;
        if (sortInfo) {
          toast.success(
            `${getSortLabel(sortInfo.key, sortInfo.order)}으로 정렬되었습니다.`
          );
        } else {
          toast.success("정렬 순서가 저장되었습니다.");
        }
      } catch (error) {
        console.error("Sequence update error:", error);
        toast.error("정렬 순서 저장에 실패했습니다.");

        // 실패 시 캐시 리프레시로 원복
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.NURSES, selectedGroupId],
        });
      } finally {
        setIsUpdating(false);
        pendingUpdateRef.current = null;
      }
    },
    [isAdmin, selectedGroupId, queryClient]
  );

  // 디바운스된 bulk update
  const scheduleBulkUpdate = useCallback(
    (
      nursesToUpdate: NurseResponse[],
      sortInfo: { key: SortKey; order: "asc" | "desc" }
    ) => {
      // 기존 타이머 클리어
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // 대기 중인 업데이트 저장
      pendingUpdateRef.current = nursesToUpdate;
      lastSortInfoRef.current = sortInfo;

      // 새 타이머 설정
      debounceTimerRef.current = setTimeout(() => {
        if (pendingUpdateRef.current) {
          executeBulkUpdate(pendingUpdateRef.current);
        }
      }, debounceMs);
    },
    [debounceMs, executeBulkUpdate]
  );

  // 정렬 핸들러
  const handleSort = useCallback(
    (key: SortKey) => {
      // API 요청 중이면 정렬 클릭 무시
      if (isUpdating) return;

      setSortConfig((prev) => {
        let newConfig: SortConfig;

        if (prev.key !== key) {
          newConfig = { key, order: "asc" };
        } else if (prev.order === "asc") {
          newConfig = { key, order: "desc" };
        } else if (prev.order === "desc") {
          newConfig = { key: null, order: null };
        } else {
          newConfig = { key, order: "asc" };
        }

        // 정렬 해제 시 sequence 업데이트 안 함
        if (!newConfig.key || !newConfig.order) {
          return newConfig;
        }

        // sequence 업데이트가 활성화된 경우에만 처리
        if (enableSequenceUpdate && nurses.length > 0) {
          // 새 정렬 기준으로 정렬
          const sorter = (a: T, b: T) => {
            if (keepRoleGroupOrder) {
              const roleDiff =
                nurseRoleSortIndex(a.role) - nurseRoleSortIndex(b.role);
              if (roleDiff !== 0) return roleDiff;
            }
            return compareNurses(
              a,
              b,
              newConfig.key!,
              newConfig.order as "asc" | "desc"
            );
          };
          const sorted = [...nurses].sort(sorter);

          // sequence 재할당 (0부터 시작)
          const updatedNurses: NurseResponse[] = sorted.map(
            (nurse, index) =>
              ({
                ...nurse,
                sequence: index,
                // Relation 필드 제거 (API 호환성)
                preceptor: undefined,
                preceptees: undefined,
              }) as NurseResponse
          );

          // 낙관적 UI 업데이트 (queryClient 캐시 직접 수정)
          queryClient.setQueryData<NurseResponse[]>(
            [QUERY_BASE_KEY.NURSES, selectedGroupId],
            (old) => {
              if (!old) return old;

              // 정렬 대상 nurse_id 목록
              const sortedIds = new Set(updatedNurses.map((n) => n.nurse_id));

              // 정렬 대상이 아닌 간호사들 보존
              const others = old.filter((n) => !sortedIds.has(n.nurse_id));

              // 업데이트된 간호사들과 기존 간호사들 병합
              return [...others, ...updatedNurses];
            }
          );

          // 디바운스된 API 호출 예약
          scheduleBulkUpdate(updatedNurses, {
            key: newConfig.key!,
            order: newConfig.order as "asc" | "desc",
          });
        }

        return newConfig;
      });
    },
    [
      enableSequenceUpdate,
      keepRoleGroupOrder,
      nurses,
      queryClient,
      selectedGroupId,
      scheduleBulkUpdate,
      isUpdating,
    ]
  );

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    sortedNurses,
    sortConfig,
    handleSort,
    isUpdating, // 업데이트 중 상태 (로딩 표시용)
  };
}
